import { LowestPriceRequest } from "../types/pricing/price-request.types";
import { ProductPriceDetails } from "../types/pricing/product-price-details.types";
import { logger } from "../util/logger";
import { retryAsync } from "../util/retry-async";

export class PricingClient {
  private baseUrl: string = "https://api.price-platform-service-cdn.pai.vpsvc.com/v3/pricing";

  async fetchProductPriceDetails(
    request: LowestPriceRequest,
  ): Promise<Record<string, ProductPriceDetails>> {
    const { locale, tenant, requestor, ...rest } = request;
    const url = `${this.baseUrl}/tenant/${tenant}/locale/${locale}/lowest-price?requestor=${requestor}`;

    const retryCondition = (error: unknown): boolean => {
      // Retry on server errors (5xx) or network errors
      return typeof error === "object" && error !== null && "status" in error &&
        (error as { status: number }).status >= 500 && (error as { status: number }).status < 600;
    };

    let response: Response;
    try {
      response = await retryAsync(async () => {
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(rest),
        });

        if (!res.ok) {
          const error = new Error(res.statusText) as Error & { status?: number };
          error.status = res.status;
          throw error;
        }

        return res;
      }, { retryCondition });
    } catch (error) {
      const err = error as Error & { status?: number };
      logger.warn("Failed to fetch product price details", { url, input: request, status: err.status, statusText: err.message });
      throw error;
    }

    const responseData: ProductPriceDetails[] = await response.json();

    return responseData.reduce<Record<string, ProductPriceDetails>>((acc, product) => {
      const { id, productId, mpvId, prices, quantityMetaData, alternateQuantityMetaData, displayStrategy, priceDisplayGroup } = product;
      acc[id] = {
        id,
        ...(productId && { productId }),
        ...(mpvId && { mpvId }),
        prices,
        quantityMetaData,
        ...(alternateQuantityMetaData && { alternateQuantityMetaData }),
        isError: !prices,
        displayStrategy,
        priceDisplayGroup,
      };
      return acc;
    }, {});
  }
}
